import { Caption, Icon, type IconName, cn } from '@eluve/components';

import { type OfferingType, TYPE_META } from './care-library-data';

// Map each offering type onto an icon + a design-system brand hue. The class
// strings are spelled out literally (never template-built) so Tailwind's
// static scanner keeps them. Hues come from the atoms theme tokens — no raw
// hex, so a token retheme carries through automatically.
interface TypeStyle {
  icon: IconName;
  /** Tinted icon-tile surface (subtle bg + brand-colored glyph). */
  mark: string;
  /** Solid brand swatch — the small square in chips/dots. */
  dot: string;
  /** Brand-colored text for the eyebrow type label. */
  label: string;
}

export const TYPE_STYLE: Record<OfferingType, TypeStyle> = {
  exercise: {
    icon: 'Dumbbell',
    mark: 'bg-bg-teal-subtle text-text-teal-subtle',
    dot: 'bg-bg-teal-strong',
    label: 'text-text-teal-subtle',
  },
  education: {
    icon: 'BookCopy',
    mark: 'bg-bg-lightblue-subtle text-text-lightblue-subtle',
    dot: 'bg-bg-lightblue-strong',
    label: 'text-text-lightblue-subtle',
  },
  treatment: {
    icon: 'Stethoscope',
    mark: 'bg-bg-yellow-subtle text-text-yellow-subtle',
    dot: 'bg-bg-yellow-strong',
    label: 'text-text-yellow-subtle',
  },
  consult: {
    icon: 'HeartHandshake',
    mark: 'bg-bg-orange-subtle text-text-orange-subtle',
    dot: 'bg-bg-orange-strong',
    label: 'text-text-orange-subtle',
  },
  group: {
    icon: 'Users',
    mark: 'bg-bg-purple-subtle text-text-purple-subtle',
    dot: 'bg-bg-purple-strong',
    label: 'text-text-purple-subtle',
  },
  visit: {
    icon: 'CalendarClock',
    mark: 'bg-bg-lightgreen-subtle text-text-lightgreen-subtle',
    dot: 'bg-bg-lightgreen-strong',
    label: 'text-text-lightgreen-subtle',
  },
  referral: {
    icon: 'Send',
    mark: 'bg-bg-gray-subtle text-text-gray-subtle',
    dot: 'bg-bg-gray-strong',
    label: 'text-text-gray-subtle',
  },
  message: {
    icon: 'MessageSquare',
    mark: 'bg-bg-info-subtle text-text-info-subtle',
    dot: 'bg-bg-info-strong-primary',
    label: 'text-text-info-subtle',
  },
};

/** Solid brand swatches the rail cycles through for service-line rows. */
export const LINE_SWATCHES = [
  'bg-bg-teal-strong',
  'bg-bg-yellow-strong',
  'bg-bg-orange-strong',
  'bg-bg-lightblue-strong',
];

/** The colored, rounded icon tile that fronts every offering. */
export const OfferingMark: React.FC<{
  type: OfferingType;
  size?: 'md' | 'lg';
  className?: string;
}> = ({ type, size = 'md', className }) => (
  <div
    className={cn(
      'flex shrink-0 items-center justify-center',
      size === 'lg' ? 'size-12 rounded-xl' : 'size-9 rounded-lg',
      TYPE_STYLE[type].mark,
      className,
    )}
  >
    <Icon name={TYPE_STYLE[type].icon} size={size === 'lg' ? 'lg' : 'md'} />
  </div>
);

/** The uppercase type eyebrow used on cards. */
export const OfferingTypeLabel: React.FC<{ type: OfferingType }> = ({
  type,
}) => (
  <Caption inline size="3xs" className={TYPE_STYLE[type].label}>
    {TYPE_META[type].short}
  </Caption>
);

/** The tinted type pill (icon + short label) used on the detail header. */
export const OfferingTypeTag: React.FC<{ type: OfferingType }> = ({ type }) => (
  <span
    className={cn(
      'inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold',
      TYPE_STYLE[type].mark,
    )}
  >
    <Icon name={TYPE_STYLE[type].icon} size="xs" />
    {TYPE_META[type].short}
  </span>
);
